import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { entrance } from "@/components/site/motionPresets";
import { PRODUCT_COLORS } from "@/components/site/productColors";

const HERO_PRODUCTS = [
  {
    name: "Daubaria",
    description: "Open testing · Google Play",
    href: "https://daubaria.onevidariadigital.com",
    logo: "/daubaria-logo.png",
    tile: { bg: "#911119", border: `${PRODUCT_COLORS.daubaria}50` },
    logoScale: "h-6 w-6",
  },
  {
    name: "Vidaria",
    description: "Botanical hub · Alpha on the web",
    href: "https://vidaria.onevidariadigital.com",
    logo: "/vidaria-logo.png",
    tile: { bg: "#FFFFFF", border: `${PRODUCT_COLORS.vidaria}55` },
    logoScale: "h-full w-full",
  },
  {
    name: "SievePulse",
    description: "Closure verification · Internal tool",
    logo: "/sievepulse-apple-touch-icon.png",
    tile: { bg: "#002B4F", border: `${PRODUCT_COLORS.sievepulse}55` },
    logoScale: "h-full w-full",
  },
];

function HeroProductRow({ product, index }) {
  const className = `flex min-h-[68px] flex-1 items-center gap-4 px-5 py-3 ${
    product.href ? "group transition-colors hover:bg-surface-2" : ""
  } ${index > 0 ? "border-t border-line" : ""}`;
  const content = (
    <>
      <span
        className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-md border"
        style={{
          backgroundColor: product.tile.bg,
          borderColor: product.tile.border,
        }}
      >
        <img
          src={product.logo}
          alt=""
          loading="lazy"
          decoding="async"
          className={`${product.logoScale} object-contain`}
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-white">
          {product.name}
        </span>
        <span className="mt-0.5 block text-xs text-white/65">
          {product.description}
        </span>
      </span>
      {product.href ? (
        <ArrowUpRight
          size={15}
          className="shrink-0 text-white/35 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#10B981]"
        />
      ) : null}
    </>
  );

  return product.href ? (
    <a
      href={product.href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      {content}
    </a>
  ) : (
    <div className={className} aria-label={`${product.name}, internal tool`}>
      {content}
    </div>
  );
}

export default function Hero() {
  const scrollToSection = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - 64,
        behavior: "smooth",
      });
    }
  };

  const scrollToEcosystem = (e) => scrollToSection(e, "ecosystem");
  const scrollToConnect = (e) => scrollToSection(e, "connect");

  return (
    <section
      id="top"
      data-testid="hero-section"
      aria-label="Introduction"
      className="relative overflow-hidden bg-surface-0 pt-32 pb-16 sm:pt-40 sm:pb-20"
    >
      <div className="relative mx-auto w-full max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-12 lg:items-stretch lg:gap-12">
          {/* Left — copy, like the Modern Corporate cards */}
          <div className="lg:col-span-7">
            <motion.p
              {...entrance({ y: 16, duration: 0.7 })}
              className="eyebrow-gold"
            >
              Software studio — Las Piñas
            </motion.p>

            <motion.h1
              {...entrance({ y: 24, duration: 0.8, delay: 0.06 })}
              data-testid="hero-headline"
              className="font-serif-display mt-5 text-balance text-5xl font-medium leading-[1.04] text-cream sm:text-6xl lg:text-7xl"
            >
              An independent studio building apps and data tools.
            </motion.h1>

            <motion.p
              {...entrance({ y: 16, duration: 0.8, delay: 0.14 })}
              data-testid="hero-subheadline"
              className="mt-7 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg"
            >
              OneVidaria Digital is a small software studio in Las Piñas,
              Philippines. Pocket Peachy is coming soon to Android, Daubaria is
              in open testing on Google Play, Vidaria is in alpha on the web,
              SievePulse is used internally, and Agos is in internal testing
              by invitation.
            </motion.p>

            <motion.div
              {...entrance({ y: 16, duration: 0.8, delay: 0.22 })}
              className="mt-10 flex flex-wrap items-center gap-3 sm:gap-4"
            >
              <a
                href="#ecosystem"
                onClick={scrollToEcosystem}
                data-testid="hero-cta-explore"
                className="group inline-flex min-h-[48px] items-center gap-3 rounded-full bg-[#10B981] px-4 py-3.5 text-sm font-semibold tracking-wide text-[#06231A] transition-colors duration-300 hover:bg-[#34D399] sm:px-7"
              >
                See the products
                <ArrowDown
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-y-0.5"
                />
              </a>
              <a
                href="#connect"
                onClick={scrollToConnect}
                data-testid="hero-cta-contact"
                className="group inline-flex min-h-[48px] items-center gap-2 rounded-full border border-line px-4 py-3.5 text-sm font-medium text-white/80 transition-colors duration-300 hover:border-line-strong hover:text-white sm:px-7"
              >
                Get in touch
                <ArrowUpRight
                  size={15}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>
            </motion.div>
          </div>

          {/* Right — layered product visual, real screenshots only. */}
          <motion.div
            {...entrance({ y: 24, duration: 0.9, delay: 0.18 })}
            className="hidden lg:col-span-5 lg:block lg:h-full"
          >
            <div className="flex h-full flex-col gap-4">
              <a
                href="https://pocketpeachy.onevidariadigital.com"
                target="_blank"
                rel="noopener noreferrer"
                className="card-premium group block transition-colors hover:bg-surface-2"
                data-testid="hero-feature-pocket-peachy"
              >
                <figure>
                  <img
                    src="/pocket-peachy-gameplay.webp"
                    alt="Pocket Peachy gameplay showing Soraya and Marvic fighting in Quick Battle."
                    width={800}
                    height={450}
                    loading="lazy"
                    decoding="async"
                    className="aspect-video w-full border-b border-line object-cover"
                  />
                  <figcaption className="flex items-center justify-between gap-4 px-5 py-4">
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-white">
                        Pocket Peachy
                      </span>
                      <span className="mt-0.5 block text-xs text-white/65">
                        Chibi action-fighter · Android
                      </span>
                    </span>
                    <span className="inline-flex shrink-0 items-center gap-2 text-[10px] font-medium uppercase tracking-[0.14em] text-white/70">
                      Coming soon
                      <ArrowUpRight
                        size={14}
                        className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                      />
                    </span>
                  </figcaption>
                </figure>
              </a>

              <div className="card-premium flex min-h-0 flex-1 flex-col">
                {HERO_PRODUCTS.map((product, index) => (
                  <HeroProductRow
                    key={product.name}
                    product={product}
                    index={index}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
